import { useEffect, useState } from 'react';
import { auditApi, SYSTEM_ACTOR_QUERY_VALUE, type AuditActorOption, type AuditRow } from '../api/audit';
import { Table, type Column } from '../components/Table';
import { t } from '../i18n';
import { formatAuditChange } from './audit-format';

const PAGE_SIZE = 50;

const fmt = (iso: string) => new Date(iso).toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' });

/**
 * `<input type="date">` gives back a bare "YYYY-MM-DD" in the viewer's local calendar, with no
 * timezone attached. Expanding it to a full ISO-8601 boundary here (in the browser, which knows
 * the viewer's UTC offset) instead of on the server means the filter always covers the viewer's
 * own local day, in whatever timezone they're in — not a UTC day that clips hours off both ends.
 */
function localDayBoundary(dateStr: string, end: boolean): string {
  const parts = dateStr.split('-').map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return end ? new Date(y, m - 1, d, 23, 59, 59, 999).toISOString() : new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

function channelLabel(channel: string | null): string {
  if (!channel) return '—';
  const key = `audit.channel.${channel}` as Parameters<typeof t>[0];
  const label = t(key);
  return label === key ? channel : label;
}

function whoLabel(row: AuditRow): string {
  if (row.actorDisplayName) return row.actorDisplayName;
  if (row.channel === 'system') return t('audit.system');
  return '—';
}

export function Audit() {
  const [actors, setActors] = useState<AuditActorOption[]>([]);
  const [resourceTypes, setResourceTypes] = useState<string[]>([]);
  const [actorId, setActorId] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    auditApi.actors().then(setActors).catch(() => undefined);
    auditApi.resourceTypes().then(setResourceTypes).catch(() => undefined);
  }, []);

  useEffect(() => {
    setError(null);
    auditApi
      .list({
        actorId: actorId || undefined,
        resourceType: resourceType || undefined,
        from: from ? localDayBoundary(from, false) : undefined,
        to: to ? localDayBoundary(to, true) : undefined,
        page,
        pageSize: PAGE_SIZE,
      })
      .then((r) => {
        setRows(r.rows);
        setTotal(r.total);
      })
      .catch((e: Error) => setError(e.message));
  }, [actorId, resourceType, from, to, page]);

  const resetPage = () => setPage(1);

  const columns: Column<AuditRow>[] = [
    { key: 'when', title: t('audit.col.when'), render: (r) => fmt(r.timestamp), sortValue: (r) => r.timestamp },
    { key: 'who', title: t('audit.col.who'), render: (r) => whoLabel(r), sortValue: (r) => whoLabel(r) },
    { key: 'action', title: t('audit.col.action'), render: (r) => r.action, sortValue: (r) => r.action },
    // The full id is in the title: on screen a uuid pushes the "change" column off the desktop viewport.
    { key: 'resource', title: t('audit.col.resource'), render: (r) => <span title={r.resourceId}>{`${r.resourceType} · ${r.resourceId.length > 12 ? `${r.resourceId.slice(0, 8)}…` : r.resourceId}`}</span>, sortValue: (r) => r.resourceType },
    { key: 'channel', title: t('audit.col.channel'), render: (r) => channelLabel(r.channel) },
    { key: 'change', title: t('audit.col.change'), render: (r) => <span className="muted wrap" style={{ display: 'inline-block', maxWidth: '32rem', overflowWrap: 'anywhere' }}>{formatAuditChange(r.before, r.after)}</span>, wrap: true },
  ];

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <h1>{t('audit.title')}</h1>
      <p className="muted">{t('audit.subtitle')}</p>
      <div className="row" style={{ marginBottom: 'var(--sp-3)' }}>
        <select
          style={{ width: 'auto' }}
          value={actorId}
          onChange={(e) => {
            setActorId(e.target.value);
            resetPage();
          }}
        >
          <option value="">{t('audit.filter.actor')}</option>
          {actors.map((a) => (
            <option key={a.actorId ?? SYSTEM_ACTOR_QUERY_VALUE} value={a.actorId ?? SYSTEM_ACTOR_QUERY_VALUE}>
              {a.displayName ?? t('audit.system')}
            </option>
          ))}
        </select>
        <select
          style={{ width: 'auto' }}
          value={resourceType}
          onChange={(e) => {
            setResourceType(e.target.value);
            resetPage();
          }}
        >
          <option value="">{t('audit.filter.resourceType')}</option>
          {resourceTypes.map((rt) => (
            <option key={rt} value={rt}>{rt}</option>
          ))}
        </select>
        <input
          className="input"
          type="date"
          style={{ width: 'auto' }}
          aria-label={t('audit.filter.from')}
          value={from}
          onChange={(e) => {
            setFrom(e.target.value);
            resetPage();
          }}
        />
        <input
          className="input"
          type="date"
          style={{ width: 'auto' }}
          aria-label={t('audit.filter.to')}
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            resetPage();
          }}
        />
      </div>
      {error && <div className="error">{error}</div>}
      <Table columns={columns} rows={rows} rowKey={(r) => r.id} empty={t('common.none')} />
      {total > PAGE_SIZE && (
        <div className="row between" style={{ marginTop: 'var(--sp-3)' }}>
          <button type="button" className="btn secondary small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t('audit.pagination.prev')}</button>
          <span className="muted">{t('audit.pagination.page', { page, pages })}</span>
          <button type="button" className="btn secondary small" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>{t('audit.pagination.next')}</button>
        </div>
      )}
      <p className="muted" style={{ marginTop: 'var(--sp-3)' }}>{t('audit.footer')}</p>
    </>
  );
}
